import {
  BiCell,
  BiFieldType,
  biLabelledCell,
  BiQueryResult,
  BiValueLabels,
} from '@euler/model/bi/result';
import { BiColumnRef } from './biColumns';

const kEmptyCategory = '(空)';

/**
 * A dimension value as a chart category or series name: a coded value's
 * label when given its column's labels; without them, the value as text,
 * what `style.seriesColors` keys a split value or a slice by.
 */
export function biCategoryLabel(
  cell: BiCell,
  valueLabels?: BiValueLabels,
): string {
  if (cell === null || cell === '') return kEmptyCategory;
  if (typeof cell === 'boolean') return cell ? '是' : '否';
  return String(biLabelledCell(cell, valueLabels));
}

/** One category of a pivot: its label, and the cell of each group. */
export interface BiPivotRow {
  category: string;
  /** The category as stored — what a copy of the table carries. */
  value: BiCell;
  cells: Map<string, BiCell>;
}

/**
 * A result with two dimensions, turned so the `by` values become columns:
 * the groups in the order they first appear, one row per `x` category.
 */
export interface BiPivot {
  /** Each group's value as text (its key); `names` says how it reads. */
  groups: string[];
  names: Map<string, string>;
  rows: BiPivotRow[];
}

function isTime(ref: BiColumnRef): boolean {
  return (
    ref.column.type === BiFieldType.Date ||
    ref.column.type === BiFieldType.Datetime
  );
}

function timeOrder(left: string, right: string): number {
  if (left === right) return 0;
  if (left === kEmptyCategory) return 1;
  if (right === kEmptyCategory) return -1;
  return left < right ? -1 : 1;
}

/**
 * Pivots the measure by the `by` dimension, the way a `seriesBy` chart
 * draws it; the chart and its result table both read this.
 */
export function biPivot(
  result: BiQueryResult,
  x: BiColumnRef,
  by: BiColumnRef,
  measure: BiColumnRef,
): BiPivot {
  const groups: string[] = [];
  const names = new Map<string, string>();
  const rows = new Map<string, BiPivotRow>();
  for (const row of result.rows) {
    // Rows and groups are keyed by the value; only `category` and `names`
    // read as labels, so two codes sharing a label stay apart.
    const key = biCategoryLabel(row[x.index]);
    const group = biCategoryLabel(row[by.index]);
    if (!names.has(group)) {
      names.set(group, biCategoryLabel(row[by.index], by.column.valueLabels));
      groups.push(group);
    }
    let line = rows.get(key);
    if (!line) {
      line = {
        category: biCategoryLabel(row[x.index], x.column.valueLabels),
        value: row[x.index],
        cells: new Map(),
      };
      rows.set(key, line);
    }
    line.cells.set(group, row[measure.index]);
  }
  const ordered = [...rows.values()];
  // Rows sorted by the series dimension first list each group's periods in
  // turn, so first appearance would put a period only one group has after
  // later ones. Time buckets are sortable labels.
  if (isTime(x)) ordered.sort((a, b) => timeOrder(a.category, b.category));
  return { groups, names, rows: ordered };
}
